import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, DollarSign, Info } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/* Admin — Pricing configuration
 * ----------------------------
 * Admin-only page to set per-case + per-additional-beneficiary fees and
 * an optional credit-card surcharge percentage. Values are stored server-side
 * in `db.settings` as a singleton doc; every /pricing/quote call reads them.
 */

const centsToDollars = (c) => (c == null ? "" : (c / 100).toFixed(2));
const dollarsToCents = (d) => Math.round(parseFloat(d || "0") * 100);

export default function AdminPricing() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      // hydrate() converts the server's cents fields into the dollars fields
      // the <Field> inputs bind to (e.g. fee_per_case_cents=34500 →
      // fee_per_case_dollars="345.00"). Without this, the inputs read
      // undefined and render as read-only-looking empty boxes — the exact
      // symptom the site owner reported.
      setForm(hydrate(data));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-14 text-center">
          <h1 className="font-display text-xl font-bold mb-2">Admin only</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Sign in with an administrator account to configure pricing.
          </p>
          <Link to="/" className="btn-primary inline-block mt-4">Back to home</Link>
        </div>
      </AppShell>
    );
  }

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = {
        fee_per_case_cents: dollarsToCents(form.fee_per_case_dollars),
        fee_per_additional_beneficiary_cents: dollarsToCents(form.fee_per_add_dollars),
        surcharge_pct: parseFloat(form.surcharge_pct || "0"),
        surcharge_disclosure: form.surcharge_disclosure || "",
        uscis_filing_fee_cents: dollarsToCents(form.uscis_filing_fee_dollars),
        notes: form.notes || "",
        ff_option_a_referrer_cents: dollarsToCents(form.ff_option_a_referrer_dollars),
        ff_option_a_referee_discount_cents: dollarsToCents(form.ff_option_a_referee_discount_dollars),
        ff_option_b_referrer_cents: dollarsToCents(form.ff_option_b_referrer_dollars),
        ff_option_b_referee_discount_cents: dollarsToCents(form.ff_option_b_referee_discount_dollars),
        affiliate_payout_per_i130_cents: dollarsToCents(form.affiliate_payout_dollars),
      };
      const { data } = await api.put("/admin/pricing", payload);
      setForm(hydrate(data));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-6 py-14 text-sm" style={{ color: "var(--text-secondary)" }}>
          Loading pricing…
        </div>
      </AppShell>
    );
  }
  // NOTE: do NOT re-hydrate `form` on every render. `form` is already hydrated
  // in load() and save(); re-running hydrate() here would overwrite the user's
  // in-progress edits (dollar fields) with the stale cents values from state,
  // making the inputs appear read-only. Bind directly to `form.*_dollars`.
  const subtotal =
    dollarsToCents(form.fee_per_case_dollars) +
    dollarsToCents(form.fee_per_add_dollars) * 2;
  const surcharge = Math.round(
    (subtotal * parseFloat(form.surcharge_pct || "0")) / 100
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-6 py-10" data-testid="admin-pricing-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
            <DollarSign className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold">Pricing</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Set the DS-260USA platform fee charged per DS-260 case. Changes apply immediately to new quotes.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/admin/directory" className="btn-ghost text-sm" data-testid="admin-pricing-directory-link">
              Directory apps →
            </Link>
            <Link to="/admin/testimonials" className="btn-ghost text-sm" data-testid="admin-pricing-testimonials-link">
              Testimonials →
            </Link>
            <Link to="/admin/testers" className="btn-ghost text-sm" data-testid="admin-pricing-testers-link">
              Manage testers →
            </Link>
          </div>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-md text-xs" style={{ background: "#FEF2F2", color: "#991B1B" }}>
            {err}
          </div>
        )}

        <div className="card-surface p-6 space-y-5">
          <Field
            label="Platform fee per DS-260 case"
            hint="What you charge for preparing the primary applicant's DS-260 case."
            value={form.fee_per_case_dollars}
            onChange={(v) => setForm({ ...form, fee_per_case_dollars: v })}
            testId="pricing-base"
            prefix="$"
          />
          <Field
            label="Additional fee per beneficiary (each child)"
            hint="Charged for every derivative beneficiary (child) added to the case beyond the primary beneficiary."
            value={form.fee_per_add_dollars}
            onChange={(v) => setForm({ ...form, fee_per_add_dollars: v })}
            testId="pricing-per-add"
            prefix="$"
          />
          <Field
            label="USCIS filing fee (display only)"
            hint="For information only — users pay this directly to USCIS with their G-1450. Update when USCIS changes its schedule."
            value={form.uscis_filing_fee_dollars}
            onChange={(v) => setForm({ ...form, uscis_filing_fee_dollars: v })}
            testId="pricing-uscis"
            prefix="$"
          />
          <hr style={{ borderColor: "var(--border-stone)" }} />
          <Field
            label="Credit-card surcharge (%)"
            hint="If set >0, credit-card payments include a pass-through surcharge (must be compliant with your state law and card-network rules). Set to 0 to absorb the fee yourself."
            value={form.surcharge_pct}
            onChange={(v) => setForm({ ...form, surcharge_pct: v })}
            testId="pricing-surcharge"
            suffix="%"
          />
          <Field
            label="Surcharge disclosure text (shown on payment page)"
            hint="Required if you charge a surcharge — the disclosure card networks require."
            value={form.surcharge_disclosure}
            onChange={(v) => setForm({ ...form, surcharge_disclosure: v })}
            testId="pricing-disclosure"
            multiline
          />
          <Field
            label="Internal notes (not shown to users)"
            hint="Optional. Track why you last changed the price, promo details, etc."
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            testId="pricing-notes"
            multiline
          />
          <hr style={{ borderColor: "var(--border-stone)" }} />
          <div className="text-xs font-semibold uppercase tracking-wide"
               style={{ color: "var(--text-secondary)" }}>Friend &amp; Family Referral (Option A default)</div>
          <Field
            label="Option A — Referrer reward"
            hint="Default option. Referrer gifts their reward — receives $0 so 100% of the value goes to the referee. Recommended default."
            value={form.ff_option_a_referrer_dollars}
            onChange={(v) => setForm({ ...form, ff_option_a_referrer_dollars: v })}
            testId="pricing-ff-a-referrer"
            prefix="$"
          />
          <Field
            label="Option A — Referee discount at checkout"
            hint="How much the friend/family member gets off at first checkout."
            value={form.ff_option_a_referee_discount_dollars}
            onChange={(v) => setForm({ ...form, ff_option_a_referee_discount_dollars: v })}
            testId="pricing-ff-a-discount"
            prefix="$"
          />
          <div className="text-xs font-semibold uppercase tracking-wide pt-2"
               style={{ color: "var(--text-secondary)" }}>Friend &amp; Family Referral (Option B — Split)</div>
          <Field
            label="Option B — Referrer payout"
            hint="Paid via Zelle / ACH once the referred case reaches CASE_ACCEPTED."
            value={form.ff_option_b_referrer_dollars}
            onChange={(v) => setForm({ ...form, ff_option_b_referrer_dollars: v })}
            testId="pricing-ff-b-referrer"
            prefix="$"
          />
          <Field
            label="Option B — Referee discount at checkout"
            hint="How much the friend/family member gets off at first checkout under Option B."
            value={form.ff_option_b_referee_discount_dollars}
            onChange={(v) => setForm({ ...form, ff_option_b_referee_discount_dollars: v })}
            testId="pricing-ff-b-discount"
            prefix="$"
          />
          <div className="text-xs font-semibold uppercase tracking-wide pt-2"
               style={{ color: "var(--text-secondary)" }}>Affiliate Referral (professional)</div>
          <Field
            label="Affiliate payout per Qualified Prime DS-260 Case"
            hint="Paid to external affiliates for each Prime DS-260 case that reaches CASE_ACCEPTED. Additional family members do NOT earn commission."
            value={form.affiliate_payout_dollars}
            onChange={(v) => setForm({ ...form, affiliate_payout_dollars: v })}
            testId="pricing-affiliate-payout"
            prefix="$"
          />
        </div>

        {/* Preview */}
        <div className="card-surface p-5 mt-4" data-testid="pricing-preview">          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
            <div className="font-display font-bold text-sm">Preview: petitioner + 2 children</div>
          </div>
          <ul className="text-sm space-y-1" style={{ color: "var(--text-primary)" }}>
            <li className="flex justify-between"><span>Platform fee — primary DS-260 case</span><span>${centsToDollars(dollarsToCents(form.fee_per_case_dollars))}</span></li>
            <li className="flex justify-between"><span>Additional beneficiary #1</span><span>${centsToDollars(dollarsToCents(form.fee_per_add_dollars))}</span></li>
            <li className="flex justify-between"><span>Additional beneficiary #2</span><span>${centsToDollars(dollarsToCents(form.fee_per_add_dollars))}</span></li>
            <li className="flex justify-between font-semibold pt-2 border-t"
                style={{ borderColor: "var(--border-stone)" }}>
              <span>Subtotal</span><span>${centsToDollars(subtotal)}</span>
            </li>
            {surcharge > 0 && (
              <li className="flex justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
                <span>+ {form.surcharge_pct}% card surcharge</span><span>${centsToDollars(surcharge)}</span>
              </li>
            )}
            <li className="flex justify-between font-bold pt-2 border-t"
                style={{ borderColor: "var(--border-stone)", color: "var(--action)" }}>
              <span>Total</span><span>${centsToDollars(subtotal + surcharge)}</span>
            </li>
          </ul>
          <p className="text-xs mt-3" style={{ color: "var(--text-secondary)" }}>
            + Users pay USCIS separately: <strong>${centsToDollars(dollarsToCents(form.uscis_filing_fee_dollars))}</strong>{" "}
            filing fee via G-1450 in the packet.
          </p>
        </div>

        <SiteConfigEditor />

        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="pricing-save"
          >
            <Save className="w-4 h-4 inline mr-1" />
            {saving ? "Saving…" : "Save pricing"}
          </button>
          {savedAt && (
            <span className="text-xs" style={{ color: "#065F46" }}>
              ✓ Saved at {savedAt}
            </span>
          )}
          {form.updated_by && !savedAt && (
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
              Last updated by {form.updated_by}
            </span>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function hydrate(server) {
  return {
    ...server,
    fee_per_case_dollars: centsToDollars(server.fee_per_case_cents),
    fee_per_add_dollars: centsToDollars(server.fee_per_additional_beneficiary_cents),
    uscis_filing_fee_dollars: centsToDollars(server.uscis_filing_fee_cents),
    surcharge_pct: (server.surcharge_pct || 0).toString(),
    // Friend & Family Referral (Canonical Spec v1.2 §2)
    ff_option_a_referrer_dollars: centsToDollars(server.ff_option_a_referrer_cents ?? 0),
    ff_option_a_referee_discount_dollars: centsToDollars(server.ff_option_a_referee_discount_cents ?? 10000),
    ff_option_b_referrer_dollars: centsToDollars(server.ff_option_b_referrer_cents ?? 2500),
    ff_option_b_referee_discount_dollars: centsToDollars(server.ff_option_b_referee_discount_cents ?? 7500),
    // Affiliate Referral (Canonical Spec v1.2 §3)
    affiliate_payout_dollars: centsToDollars(server.affiliate_payout_per_i130_cents ?? 20000),
  };
}

function Field({ label, hint, value, onChange, testId, prefix, suffix, multiline }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="flex items-center gap-2">
        {prefix && <span style={{ color: "var(--text-secondary)" }}>{prefix}</span>}
        {multiline ? (
          <textarea
            className="field-input font-mono text-sm"
            rows={2}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
        ) : (
          <input
            className="field-input"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
        )}
        {suffix && <span style={{ color: "var(--text-secondary)" }}>{suffix}</span>}
      </div>
      {hint && (
        <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{hint}</p>
      )}
    </div>
  );
}


/* ---------- Site Config (hero video URL) ---------- */
function SiteConfigEditor() {
  const [url, setUrl] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/admin/site-config")
      .then(({ data }) => { setUrl(data.hero_video_url || ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/site-config", { hero_video_url: url.trim() });
      setUrl(data.hero_video_url || "");
      setSavedAt(new Date().toISOString());
    } catch (e) {
      // Non-critical: site-config save failed. Log so we can debug, but keep
      // the field populated so the user can retry without losing their input.
      console.error("[AdminPricing] site-config save failed:", e);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="card-surface p-5 mt-4" data-testid="site-config-editor">
      <div className="flex items-center gap-2 mb-3">
        <Info className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        <div className="font-display font-bold text-sm">Homepage walkthrough video</div>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        Paste a YouTube, Vimeo, Loom, or direct <code>.mp4</code> URL. It appears on the
        homepage hero with a Play button overlay. Leave blank to show the interactive
        <code> /demo </code>fallback instead.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="url"
          className="field-input flex-1"
          placeholder="https://www.loom.com/share/… or https://youtu.be/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="site-config-video-url"
        />
        <button
          type="button"
          className="btn-primary text-sm"
          disabled={saving}
          onClick={save}
          data-testid="site-config-save"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {savedAt && (
        <p className="text-[11px] mt-2" style={{ color: "var(--text-secondary)" }}>
          Saved · homepage will show the new video on next visit.
        </p>
      )}
    </div>
  );
}
